<script lang="ts">
	import { t } from '#lib/i18n/tranlsator.svelte.ts';
	import type { AntonioInput, AntonioStepView } from '#src/types/antonio-step.ts';
	import AntonioButtons from './AntonioButtons.svelte';
	import AntonioChoiceInput from './AntonioChoiceInput.svelte';
	import AntonioContactInput from './AntonioContactInput.svelte';
	import AntonioImageUpload from './AntonioImageUpload.svelte';

	interface Props {
		/** The step to render, as the server named it. Kinds without controls of their own (text, start) render nothing. */
		step: AntonioStepView;
		/**
		 * Answers the step. displayText is the visitor's bubble, which the server
		 * also stores as their turn for the LLM. A choice step's pick has none:
		 * the chat shows it as a pick, and the server writes that turn itself.
		 * Resolves to whether the input went through.
		 */
		onInput: (input: AntonioInput, displayText?: string) => Promise<boolean>;
		onUploadImage: (file: File) => Promise<void>;
		onRestart: () => void;
	}

	const { step, onInput, onUploadImage, onRestart }: Props = $props();

	/** The inputs the controls word themselves: start and free text have no fixed wording, a pick isn't worded at all. */
	type StepInput = Exclude<AntonioInput, { type: 'start' | 'text' | 'choice' }>;

	/** The visitor's bubble for an input, the one place each input's wording is decided. */
	function answerDisplayText(input: StepInput): string {
		switch (input.type) {
			case 'confirm_generate':
				return input.confirmed
					? t('antonio.generateConfirm.yes.value')
					: t('antonio.generateConfirm.no.value');
			case 'publish':
				return input.confirmed
					? t('antonio.publishConfirm.yes.value')
					: t('antonio.publishConfirm.no.value');
			case 'contact':
				return input.email
					? t('antonio.contact.submitted.nameEmail')
					: t('antonio.contact.submitted.name');
		}
	}

	function answer(input: StepInput) {
		return onInput(input, answerDisplayText(input));
	}

	/**
	 * Both confirmations in this flow are fixed yes/no choices — the model never composes button copy itself.
	 * Without allowChanges, only the yes option is left.
	 */
	function confirmOptions(yesLabel: string, noLabel: string) {
		const allowChanges = 'allowChanges' in step && step.allowChanges;
		return [
			{ label: yesLabel, value: true, primary: true },
			...(allowChanges ? [{ label: noLabel, value: false }] : [])
		];
	}

	async function submitContact(name: string, email?: string, emailOptIn?: boolean) {
		// Thrown so the form stays open for another try; the chat shows the error itself.
		if (!(await answer({ type: 'contact', name, email, emailOptIn }))) {
			throw new Error('Contact not submitted');
		}
	}
</script>

{#if step.kind === 'choice'}
	<!-- Keyed so a multiple-choice selection never carries over to the next step. -->
	{#key step.key}
		<AntonioChoiceInput {step} onsubmit={(optionIds) => onInput({ type: 'choice', optionIds })} />
	{/key}
{:else if step.kind === 'confirm-generate'}
	<AntonioButtons
		options={confirmOptions(
			t('antonio.generateConfirm.yes.label'),
			t('antonio.generateConfirm.no.label')
		)}
		onselect={(confirmed) => answer({ type: 'confirm_generate', confirmed })}
	/>
{:else if step.kind === 'image'}
	<!-- The image step replaces the generate-confirm step rather than adding one:
	     both outcomes (image uploaded, or skipped) are the visitor saying "go
	     ahead", so both continue with the same confirmation. -->
	<AntonioImageUpload
		onsubmit={async (file) => {
			await onUploadImage(file);
			answer({ type: 'confirm_generate', confirmed: true });
		}}
		onskip={() => answer({ type: 'confirm_generate', confirmed: true })}
	/>
{:else if step.kind === 'confirm-publish'}
	<AntonioButtons
		options={confirmOptions(
			t('antonio.publishConfirm.yes.label'),
			t('antonio.publishConfirm.no.label')
		)}
		onselect={(confirmed) => answer({ type: 'publish', confirmed })}
	/>
{:else if step.kind === 'contact'}
	<AntonioContactInput
		showEmailInput={step.showEmailInput}
		showNewsletterCheckbox={step.showNewsletterCheckbox}
		onsubmit={submitContact}
		ondecline={step.allowChanges ? () => answer({ type: 'publish', confirmed: false }) : undefined}
	/>
{:else if step.kind === 'end'}
	<div class="restart">
		{#if step.galleryUrl}
			<a class="button accent large" href={step.galleryUrl} target="_blank" rel="noopener">
				{t('antonio.viewInGallery')}
			</a>
		{/if}
		<button class=" large" onclick={onRestart}>{t('antonio.startOver')}</button>
	</div>
{/if}

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.restart {
		@include lib.flex-row();
		@include lib.flex-align(center);
		gap: 0.5rem;
		padding: 0.5rem 0;
		> a,
		> button {
			flex: 1 1 auto;
		}
	}
</style>
